import type { JSONValue } from '@common/interfaces/customControlData';
import type { IProcessActivityReadSection } from '@common/interfaces/processActivityReadSection';

export interface ITextBlockItem {
    label: string;
    value: JSONValue;
}

export interface ITextBlockItemForParagraph {
    label: string | null;
    value?: string | null | ITextBlockItem[];
}

export function addTextBlockWithEachItemAsParagraph(
    text: string,
    header: string | null,
    items: ITextBlockItemForParagraph[]
): string {
    text += header ? `\n\n\n${header}:` : '';
    items.forEach((item) => (text = addParagraph(text, item)));

    return text;
}

function addParagraph(text: string, item: ITextBlockItemForParagraph) {
    const value = item.value ?? null;
    text += item.label ? `\n\n${item.label}:` : '\n';
    if (Array.isArray(value)) {
        value.forEach((subItem) => (text += `\n-${subItem.label}: ${subItem.value}`));

        return text;
    }

    return (text += `\n${value}`);
}

export function addTextBlockWithEachItemOnOneLine(
    text: string,
    header: string | null,
    items: ITextBlockItem[]
): string {
    if (items.every((item) => item.value === null) && !header) {
        return text;
    }

    text += header ? `\n\n${header.toUpperCase()}:` : '\n';
    items
        .filter((item) => item.value !== null)
        .forEach((item) => {
            if (Array.isArray(item.value)) {
                text += `\n${item.label}:`;
                item.value.forEach((value) => (text += `\t${value}`));
            } else {
                text += `\n${item.label}: ${getValueAsString(item.value)}`;
            }
        });

    return text;
}

function getValueAsString(value: JSONValue): string {
    // Replace \n with '\n\t for better readability
    return `${value}`.replace(/\n/g, '\n\t');
}

export function addTextBlockForReadSection<SectionTitle extends string, Key extends string>({
    header,
    keys,
    readSections,
    sectionTitle,
    text,
}: {
    header?: string;
    keys?: Key[];
    readSections: IProcessActivityReadSection[];
    sectionTitle: SectionTitle;
    text: string;
}) {
    const readSection = readSections.find(
        (section: IProcessActivityReadSection) => section.sectionTitle === sectionTitle
    );

    let items = readSection?.items ?? [];
    if (keys) {
        items = items.filter((item) => keys.includes(item.key as Key));
    }

    return addTextBlockWithEachItemOnOneLine(text, header || sectionTitle, items);
}
